import * as React from 'react';
import * as moment from 'moment';
import * as numeral from 'numeral';
import assign = require('object-assign');

import ISettings from './ISettings';

import { getDisplayValue, combineCamelCase } from '../../util/StringUtil';
import { calcStats } from '../../util/StateUtil';
import { getDaysBetween, getDateId } from '../../util/DateUtil';

interface IProps {
    node: any,
    states: any[],
    startDate: Date,
    endDate: Date,
    settings: ISettings,
}

const AVAILABLE_COLS = [
    'name',
    'alertDuration',
    'okDuration',
    'dayDistribution',
    'alertPercentage',
    'okPercentage',
];

export default class SummaryTable extends React.Component<IProps, void> {
    static propTypes = {
        node: React.PropTypes.object.isRequired,
        states: React.PropTypes.array,
        startDate: React.PropTypes.object.isRequired,
        endDate: React.PropTypes.object.isRequired,
        settings: React.PropTypes.object.isRequired,
    };

    calcStateStats(node: any, states: any[], startDate: Date, endDate: Date) {
        states = states || [];

        const stats = calcStats(states, startDate, endDate);

        const days = {},
            filtered = {
                errors: [],
                success: []
            },
            daysInTimespan = getDaysBetween(startDate, endDate),
            startMs = startDate.getTime(),
            endMs = endDate.getTime();

        let lastStatusStart;

        for (let i = 0; i < daysInTimespan; i++) {
            const currentDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + i),
                dayId = getDateId(currentDate);

            days[dayId] = {
                errors: [],
                success: [],
                date: currentDate
            }
        }

        states.forEach((state) => {
            let statusStart, statusEnd, statusTimespanDays, dayStatusProperty;

            if (state.date.getTime() < startMs) {
                statusStart = startDate;
            } else {
                statusStart = state.date;
            }

            if (state.lastDate.getTime() > endMs) {
                statusEnd = endDate;
            } else {
                statusEnd = lastStatusStart || state.lastDate;
            }

            statusTimespanDays = getDaysBetween(statusStart, statusEnd) || 1;
            dayStatusProperty = state.state ? 'errors' : 'success';

            for (let j = 0; j < statusTimespanDays; j++) {
                const currentDate = new Date(statusStart.getFullYear(), statusStart.getMonth(), statusStart.getDate() + j),
                    dayId = getDateId(currentDate);

                if (days[dayId] && days[dayId][dayStatusProperty]) {
                    days[dayId][dayStatusProperty].push(state);
                }
            }

            lastStatusStart = statusStart;
        });



        return assign({
            days: days,
        }, stats);
    }

    renderDayBoxes(stats: any, startDate: Date, endDate: Date) {
        const dayBoxes = [];
        const days = getDaysBetween(startDate, endDate);

        for (let i = 0; i < days; i++) {
            const key = `day-${i}`;
            const currMoment = moment(startDate).add(i, 'days');
            const currDate = currMoment.toDate();
            const dayId = getDateId(currDate);

            const classNames = [
                'fa',
                'small',
            ];

            // is saturday
            if (currMoment.isoWeekday() === 6) {
                classNames.push('margin-left-xs');
            }
            // is sunday
            if (currMoment.isoWeekday() === 7) {
                classNames.push('margin-right-xs');
            }

            if (stats.days[dayId] && stats.days[dayId].errors.length) {
                classNames.push('fa-times-circle');
                classNames.push('text-danger');
            } else {
                classNames.push('fa-circle');
                classNames.push('text-success');
            }

            dayBoxes.push(<i key={key} className={classNames.join(' ')} />);
        }

        return dayBoxes;
    }

    renderAlertDuration(name: string, stats) {
        const minInError = stats.msInError / 1000;
        return (<td key={name} className="text-right">{numeral(minInError).format('00:00:00')}</td>);
    }

    renderOkDuration(name: string, stats) {
        const msInOK = (stats.msTotal - stats.msInError);
        const minInOK = msInOK / 1000;
        return (<td key={name} className="text-right">{numeral(minInOK).format('00:00:00')}</td>);
    }

    renderDayDistribution(name: string, stats, startDate: Date, endDate: Date) {
        return (<td key={name} className="text-right">{this.renderDayBoxes(stats, startDate, endDate)}</td>);
    }

    renderAlertPercentage(name: string, stats) {
        const percInError = stats.msInError / stats.msTotal;
        return (<td key={name} className="text-right">{numeral(percInError).format('0.00%')}</td>);
    }

    renderOkPercentage(name: string, stats) {
        const msInOK = (stats.msTotal - stats.msInError);
        const percInOK = msInOK / stats.msTotal;
        return (<td key={name} className="text-right">{numeral(percInOK).format('0.00%')}</td>);
    }

    renderColumn(name: string, settings: ISettings, stats, startDate: Date, endDate: Date) {
        const renderFunc = combineCamelCase('render', name);

        if (getDisplayValue(name, settings)) {
            return this[renderFunc](name, stats, startDate, endDate);
        }

        return;
    }

    render() {
        const { node, states, startDate, endDate, settings } = this.props;
        const stats = this.calcStateStats(node, states, startDate, endDate);

        return (
            <tr key={node.id}>
                <td>{node.name}</td>
                {AVAILABLE_COLS.map(name => {
                    return this.renderColumn(name, settings, stats, startDate, endDate);
                })}
            </tr>
        );
    }
}


// WEBPACK FOOTER //
// ./src/components/widget/alerts/listAlertSummary/SummaryRow.tsx